/**
 * Rewards / incentives client (hand-rolled).
 *
 * The /incentives module is NEW — a parallel backend agent is building it against
 * the canonical contract below — and orval has NOT been regenerated for it yet, so
 * these functions are hand-rolled on the shared axios mutator (src/api/client.ts →
 * fseAxios), the same pattern as src/api/buyers.ts / photos.ts / provenance.ts.
 * They return the response BODY directly (fseAxios unwraps `response.data`) and
 * raise FseApiError on a non-2xx so TanStack Query routes them to its error path.
 *
 * CANONICAL CONTRACT (single source of truth until orval regen):
 *   GET  /incentives/me/balance              → { balance: number }   (integer points; default 0)
 *   GET  /incentives/me/ledger?limit&offset  → { data: LedgerEntry[] } (enveloped {data:[]}, newest first)
 *   POST /incentives/redeem                   → HTTP 501 (not implemented; redemption opens at mainnet)
 *
 * MONEY/POINTS: points are plain integers, NOT micro-ZARP — no microToZarp here.
 */
import { FseApiError, fseAxios } from './client';

/** Ledger entry kind — the reason a points delta was recorded. */
export type IncentiveKind =
  | 'data_quality'
  | 'participation'
  | 'referral'
  | 'redemption';

/** A single points-ledger row (newest first on the wire). */
export interface IncentiveLedgerEntry {
  id: string;
  kind: IncentiveKind;
  /** Signed integer points delta: positive = earned, negative = redeemed. */
  amountPoints: number;
  /** Free-form reference to the originating event (contribution id, order id, …). */
  reasonRef: string | null;
  /** ISO 8601 timestamp the entry was recorded. */
  createdAt: string;
}

/** GET /incentives/me/balance response. */
export interface IncentiveBalance {
  /** Current points balance (integer; defaults to 0 for a brand-new account). */
  balance: number;
}

/** GET /incentives/me/ledger response — the standard {data:[]} envelope. */
export interface IncentiveLedgerList {
  data: IncentiveLedgerEntry[];
}

/** Query params for the ledger page (limit/offset; both optional). */
export interface IncentiveLedgerParams {
  limit?: number;
  offset?: number;
}

/**
 * Read the signed-in user's current points balance. Authed (bearer injected by
 * the mutator). A brand-new account has a 0 balance.
 */
export function getIncentiveBalance(signal?: AbortSignal): Promise<IncentiveBalance> {
  return fseAxios<IncentiveBalance>({
    url: '/incentives/me/balance',
    method: 'GET',
    signal,
  });
}

/**
 * Read a page of the signed-in user's points ledger (newest first). Authed. The
 * response is the standard {data:[]} envelope — consumers read `.data`.
 */
export function getIncentiveLedger(
  params: IncentiveLedgerParams = { limit: 50, offset: 0 },
  signal?: AbortSignal,
): Promise<IncentiveLedgerList> {
  return fseAxios<IncentiveLedgerList>({
    url: '/incentives/me/ledger',
    method: 'GET',
    params,
    signal,
  });
}

/**
 * Attempt to redeem points. The endpoint is intentionally NOT implemented yet —
 * it returns HTTP 501 until redemption opens at mainnet launch. The screen renders
 * a disabled "opens at mainnet" affordance and does NOT call this; it's exported
 * for completeness + a future enable.
 */
export function redeemIncentives(signal?: AbortSignal): Promise<unknown> {
  return fseAxios<unknown>({
    url: '/incentives/redeem',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: {},
    signal,
  });
}

/**
 * True when an error is the expected HTTP 501 from /incentives/redeem (redemption
 * not yet open). The screen uses this to render the disabled state instead of an
 * error toast.
 */
export function isRedeemNotImplemented(err: unknown): boolean {
  return err instanceof FseApiError && err.status === 501;
}
